import type { Element as XmlEl } from "@xmpp/xml"
import { reportChatError } from "../../../reportChatError"
import type {
  AttachmentRef,
  ChatMessage,
  Message,
  MessageRouting,
  SharedItemRef,
} from "../../../types"
import { InvalidStanza, UnsupportedEventStanza } from "./exceptions"
import { unwrap } from "./unwrap"
import { attr } from "../../xmlUtils"
import { nanoid } from "nanoid"
import {
  NS_ATTENSI_ATTACHMENT,
  NS_ATTENSI_CHAT,
  NS_ATTENSI_SHARE,
  NS_DELAY,
  NS_SID,
} from "../../constants"
import { isTombstone, parseTombstone } from "./tombstone/parse"
import { isRetraction, parseRetraction } from "./retraction/parse"
import { isEvent, parseEvent } from "./event/parse"
import { isJid, isRoomJid, toJid } from "../../jid"

export function parse(potentiallyWrappedStanza: XmlEl): Message | null {
  try {
    const xml = unwrap(potentiallyWrappedStanza)

    if (isTombstone(xml)) return { ...common(xml), ...parseTombstone(xml) }
    if (isRetraction(xml)) return { ...common(xml), ...parseRetraction(xml) }
    if (isEvent(xml)) return { ...common(xml), ...parseEvent(xml) }

    return parseChat(xml) // fallback: parse and render as a normal chat message
  } catch (error: unknown) {
    if (error instanceof UnsupportedEventStanza) return parseChat(error.stanza)
    if (error instanceof InvalidStanza) return onInvalidStanza(error)

    throw error // our own bug, not the server's — do not swallow it
  }
}

function parseChat(stanza: XmlEl): ChatMessage {
  return {
    ...common(stanza),
    type: "chat",
    retracted: false,
    share: share(stanza),
    attachment: attachment(stanza),
  }
}

/**
 * The image off the `<attachment>` marker. A marker missing its id or a
 * positive size is treated as absent: without a size the thread cannot lay
 * the image out.
 */
function attachment(stanza: XmlEl): AttachmentRef | undefined {
  const el = stanza.getChild("attachment", NS_ATTENSI_ATTACHMENT)
  if (!el) return undefined

  const id = attr(el, "id")
  const width = Number(attr(el, "width"))
  const height = Number(attr(el, "height"))
  if (!id || !(width > 0) || !(height > 0)) return undefined

  return { id, width, height }
}

/**
 * The shared-item reference (FAME-237), off the `<share>` marker. Only the
 * reference is on the wire — the receiver resolves it to a card (FAME-453). A
 * marker missing a field, or naming an item type we do not know, is treated as
 * absent so the message falls back to its deep-link body.
 */
function share(stanza: XmlEl): SharedItemRef | undefined {
  const el = stanza.getChild("share", NS_ATTENSI_SHARE)
  if (!el) return undefined

  const type = attr(el, "type")
  const id = attr(el, "id")
  if (type !== "product" || !id) return undefined

  return { type, id }
}

/** Fiels common to all Message types */
function common(stanza: XmlEl) {
  return {
    id: originId(stanza) ?? stanzaId(stanza) ?? attr(stanza, "id") ?? nanoid(),
    roomStanzaId: roomStanzaId(stanza),
    author: author(stanza),
    body: body(stanza),
    timestamp: timestamp(stanza),
    routing: messageRouting(stanza),
  }
}

function originId(stanza: XmlEl) {
  return attr(stanza.getChild("origin-id", NS_SID), "id")
}

function stanzaId(stanza: XmlEl) {
  return attr(stanza.getChild("stanza-id", NS_SID), "id")
}

function roomStanzaId(stanza: XmlEl) {
  const stamp = stanza.getChildren("stanza-id", NS_SID).find((el) => {
    const by = attr(el, "by")
    return by !== undefined && isJid(by) && isRoomJid(toJid(by))
  })

  return attr(stamp, "id")
}

function body(stanza: XmlEl) {
  return stanza.getChildText("body") ?? undefined
}

function timestamp(stanza: XmlEl): number {
  const stamp = attr(stanza.getChild("delay", NS_DELAY), "stamp")
  return stamp ? new Date(stamp).getTime() : Date.now()
}

function author(stanza: XmlEl) {
  return attr(stanza.getChild("author", NS_ATTENSI_CHAT), "handle")
}

function messageRouting(stanza: XmlEl): MessageRouting {
  const from = attr(stanza, "from")
  const to = attr(stanza, "to")
  if (!from || !to) throw new InvalidStanza("stanza carries no routing", stanza)

  return { from: toJid(from), to: toJid(to) }
}

function onInvalidStanza(error: InvalidStanza) {
  reportChatError("unreadable stanza, skipping", error)

  return null
}
